import { useMemo } from 'react';
import { CopyIcon, GitCommitIcon, WarningIcon } from '@phosphor-icons/react';

import { useGitCommits } from '@/api-models/sandbox-git';
import { Alert } from '@/ui/Alert';
import { Button } from '@/ui/button/Button';
import { Spinner } from '@/ui/Spinner';
import { formatTimeAgo } from '@/utils/format';

import { copyToClipboard } from './copy-to-clipboard';
import { RequestError } from './RequestError';
import { StaffDetails } from './StaffDetails';

/** Round author avatar, falling back to a monogram when the provider gives no image. */
function CommitAvatar({ url, name }: { url: string | null; name: string }) {
  if (url) {
    return <img src={url} alt="" className="size-6 shrink-0 rounded-full bg-surface-3 object-cover" />;
  }
  const initial = name.trim().charAt(0).toUpperCase() || '?';
  return (
    <span
      aria-hidden
      className="flex size-6 shrink-0 items-center justify-center rounded-full bg-surface-3 text-2xs font-semibold text-fg-secondary"
    >
      {initial}
    </span>
  );
}

/** Commits on the session branch as the provider sees them, newest first. */
export function CommitsList({ base, baseRef }: { base: string; baseRef: string }) {
  const { commits, unavailable, isLoading, error } = useGitCommits(base, baseRef);

  // Provider `compare` returns oldest-first; reviewers read newest-first, and the tip is the one
  // worth flagging as the branch's current HEAD.
  const ordered = useMemo(() => [...commits].reverse(), [commits]);

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner size={5} />
      </div>
    );
  }

  if (error) return <RequestError error={error} what="commits" />;

  if (commits.length === 0) {
    // `unavailable` is the provider's own refusal, kept verbatim: "push the branch" is the
    // overwhelmingly common cause, but not the only one, and guessing would hide the rest.
    return unavailable ? (
      <Alert variant="warning" icon={<WarningIcon size={14} />} className="m-3 p-3 text-xs">
        Commits are read from the provider, and this branch is not there yet — push it, then reload.
        <StaffDetails details={unavailable} />
      </Alert>
    ) : (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center text-sm text-fg-tertiary">
        <GitCommitIcon size={24} className="text-fg-tertiary" />
        No commits on this branch yet.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-3">
      <div className="flex items-center justify-between px-1">
        <span className="text-2xs font-semibold uppercase tracking-wide text-fg-tertiary">
          {ordered.length} commit{ordered.length === 1 ? '' : 's'}
        </span>
      </div>
      {ordered.map((commit, index) => (
        <div
          key={commit.sha}
          className="flex items-center gap-3 rounded-lg border border-line-subtle bg-surface-2 px-3 py-2 transition-colors hover:border-line"
        >
          <CommitAvatar url={commit.author_avatar_url} name={commit.author_name} />
          <a
            href={commit.html_url}
            target="_blank"
            rel="noreferrer"
            className="min-w-0 flex-1 outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
          >
            <div className="flex items-center gap-2">
              <span className="truncate text-xs text-fg">{commit.message.split('\n')[0]}</span>
              {index === 0 && (
                <span className="shrink-0 rounded-full bg-diff-add-bg px-1.5 py-0.5 text-4xs font-medium text-diff-add">
                  Latest
                </span>
              )}
            </div>
            <div className="mt-0.5 truncate text-2xs text-fg-tertiary">
              {commit.author_name} · {formatTimeAgo(commit.committed_at)}
            </div>
          </a>
          <span className="shrink-0 font-mono text-2xs text-fg-tertiary">{commit.sha.slice(0, 7)}</span>
          <Button
            variant="ghost"
            shape="square"
            size={7}
            onClick={() => copyToClipboard(commit.sha, 'Commit SHA copied')}
            aria-label="Copy commit SHA"
            title="Copy full SHA"
          >
            <CopyIcon size={12} />
          </Button>
        </div>
      ))}
    </div>
  );
}
